import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  Tooltip,
  CardActions
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon
} from '@mui/icons-material';
import { api } from '../utils/api';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  buildFitColgroup,
  fitTruncateSx,
  fitChipSx,
} from '../utils/fitTable';

interface Company {
  id: number;
  name: string;
  nickname?: string;
  company_type: string;
  country?: string;
  registration_number?: string;
  tax_id?: string;
  address?: string;
  omar_ownership: number;
  heidi_ownership: number;
  dwayne_ownership: number;
  sean_ownership: number;
  lena_ownership: number;
  business_description?: string;
  website?: string;
  contact_email?: string;
  contact_phone?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const Companies: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    nickname: '',
    company_type: '',
    country: '',
    omar_director: false,
    omar_ownership: '',
    heidi_director: false,
    heidi_ownership: ''
  });
  const [viewMode, setViewMode] = useState<'table' | 'tile'>('table');
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/companies');
      setCompanies(response.data.companies);
    } catch (err: any) {
      setError('Failed to load companies');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingCompany) {
        await api.put(`/api/companies/${editingCompany.id}`, formData);
      } else {
        await api.post('/api/companies', formData);
      }
      setOpen(false);
      setEditingCompany(null);
      setFormData({
        name: '',
        nickname: '',
        company_type: '',
        country: '',
        omar_director: false,
        omar_ownership: '',
        heidi_director: false,
        heidi_ownership: ''
      });
      fetchCompanies();
    } catch (err: any) {
      setError('Failed to save company');
      console.error(err);
    }
  };

  const handleEdit = (company: Company) => {
    setEditingCompany(company);
    setFormData({
      name: company.name,
      nickname: company.nickname || '',
      company_type: company.company_type,
      country: company.country || '',
      omar_director: false, // Default value since this field doesn't exist in backend
      omar_ownership: company.omar_ownership.toString() || '0',
      heidi_director: false, // Default value since this field doesn't exist in backend
      heidi_ownership: company.heidi_ownership.toString() || '0'
    });
    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await api.delete(`/api/companies/${id}`);
        fetchCompanies();
      } catch (err: any) {
        setError('Failed to delete company');
        console.error(err);
      }
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedCompanies(companies.map(c => c.id));
    } else {
      setSelectedCompanies([]);
    }
  };

  const handleSelectOne = (companyId: number) => {
    setSelectedCompanies(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedCompanies.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedCompanies.length} selected companies?`)) {
      return;
    }

    try {
      setError('');
      const deletePromises = selectedCompanies.map(companyId => 
        api.delete(`/api/companies/${companyId}`)
      );
      
      await Promise.all(deletePromises);
      alert(`Successfully deleted ${selectedCompanies.length} companies`);
      setSelectedCompanies([]);
      fetchCompanies();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete some companies');
    }
  };

  const handleClose = () => {
    setOpen(false);
    setEditingCompany(null);
    setFormData({
      name: '',
      nickname: '',
      company_type: '',
      country: '',
      omar_director: false,
      omar_ownership: '',
      heidi_director: false,
      heidi_ownership: ''
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Companies 🏢
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {selectedCompanies.length > 0 && viewMode === 'table' && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
              >
                Delete Selected ({selectedCompanies.length})
              </Button>
            )}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(e, newMode) => newMode && setViewMode(newMode)}
              size="small"
            >
              <ToggleButton value="table">
                <ViewListIcon />
              </ToggleButton>
              <ToggleButton value="tile">
                <ViewModuleIcon />
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCompanies}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpen(true)}
            >
              Add Company
            </Button>
          <Button
            variant="outlined"
            component="label"
            startIcon={<BusinessIcon />}
          >
            Import Companies
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  console.log('Companies import file selected:', file.name);
                  // TODO: Implement companies import logic
                }
              }}
            />
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {companies.length === 0 ? (
          <Card>
            <CardContent>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <BusinessIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No companies yet.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Add your first company to get started.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : viewMode === 'table' ? (
          <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={FIT_TABLE_SX}>
                  <FitColgroup
                    cols={buildFitColgroup([
                      { key: 'select', pct: 3 },
                      { key: 'name' },
                      { key: 'nickname', pct: 15 },
                      { key: 'type', pct: 12 },
                      { key: 'country', pct: 12 },
                      { key: 'omar', pct: 8 },
                      { key: 'heidi', pct: 8 },
                      { key: 'actions', pct: 8 },
                    ])}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedCompanies.length === companies.length && companies.length > 0}
                          indeterminate={selectedCompanies.length > 0 && selectedCompanies.length < companies.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Nickname</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Country</TableCell>
                      <TableCell>Omar</TableCell>
                      <TableCell>Heidi</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companies.map((company) => (
                      <TableRow 
                        key={company.id} 
                        hover
                        selected={selectedCompanies.includes(company.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedCompanies.includes(company.id)}
                            onChange={() => handleSelectOne(company.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2" fontWeight="bold" sx={fitTruncateSx} title={company.name}>
                            {company.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={fitTruncateSx} title={company.nickname || ''}>
                            {company.nickname || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={company.company_type} 
                            color="primary" 
                            size="small" 
                            sx={fitChipSx}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={company.country} 
                            color={company.country === 'Ireland' ? 'success' : 'info'} 
                            size="small" 
                            sx={fitChipSx}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {(company.omar_ownership * 100).toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" display="block">
                            {(company.heidi_ownership * 100).toFixed(1)}%
                          </Typography>
                        </TableCell>
                        <TableCell className="fit-table-no-clip">
                          <IconButton onClick={() => handleEdit(company)} size="small">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(company.id)} size="small" color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
            {companies.map((company) => (
              <Card key={company.id} sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon />
                      <Typography variant="h6" component="h3">
                        {company.name}
                      </Typography>
                    </Box>
                    <Checkbox
                      checked={selectedCompanies.includes(company.id)}
                      onChange={() => handleSelectOne(company.id)}
                    />
                  </Box>
                  
                  {company.nickname && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Nickname
                      </Typography>
                      <Typography variant="body1">
                        {company.nickname}
                      </Typography>
                    </Box>
                  )}
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Type
                    </Typography>
                    <Chip 
                      label={company.company_type} 
                      color="primary" 
                      size="small" 
                    />
                  </Box>
                  
                  {company.country && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Country
                      </Typography>
                      <Chip 
                        label={company.country} 
                        color="secondary" 
                        size="small" 
                      />
                    </Box>
                  )}
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Ownership
                    </Typography>
                    <Typography variant="body2">
                      Omar: {(company.omar_ownership * 100).toFixed(1)}% | Heidi: {(company.heidi_ownership * 100).toFixed(1)}%
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                  <Tooltip title="Edit Company">
                    <IconButton onClick={() => handleEdit(company)} size="small">
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Company">
                    <IconButton onClick={() => handleDelete(company.id)} size="small" color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}

        {/* Add/Edit Dialog */}
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {editingCompany ? 'Edit Company' : 'Add New Company'}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <TextField
                  label="Company Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  fullWidth
                />
                <TextField
                  label="Nickname"
                  value={formData.nickname}
                  onChange={(e) => setFormData({...formData, nickname: e.target.value})}
                  fullWidth
                />
                <FormControl fullWidth required>
                  <InputLabel>Company Type</InputLabel>
                  <Select
                    value={formData.company_type}
                    onChange={(e) => setFormData({...formData, company_type: e.target.value})}
                    label="Company Type"
                  >
                    <MenuItem value="Parent Company">Parent Company</MenuItem>
                    <MenuItem value="SPV subsidiary">SPV subsidiary</MenuItem>
                    <MenuItem value="Trading Company">Trading Company</MenuItem>
                    <MenuItem value="Holding Company">Holding Company</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth required>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={formData.country}
                    onChange={(e) => setFormData({...formData, country: e.target.value})}
                    label="Country"
                  >
                    <MenuItem value="Ireland">Ireland</MenuItem>
                    <MenuItem value="UK">UK</MenuItem>
                    <MenuItem value="USA">USA</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Omar</Typography>
                    <TextField
                      label="Ownership %"
                      value={formData.omar_ownership}
                      onChange={(e) => setFormData({...formData, omar_ownership: e.target.value})}
                      placeholder="e.g., 50%"
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>Heidi</Typography>
                    <TextField
                      label="Ownership %"
                      value={formData.heidi_ownership}
                      onChange={(e) => setFormData({...formData, heidi_ownership: e.target.value})}
                      placeholder="e.g., 50%"
                      fullWidth
                    />
                  </Box>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Cancel</Button>
              <Button type="submit" variant="contained">
                {editingCompany ? 'Update' : 'Create'} Company
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Box>
    </Container>
  );
};
